import React, { useCallback, useContext, useMemo, useRef } from "react";
import ScreenCard, { ScreenCardHandle } from "../ScreenCard";
import { StackScreenProps } from "@react-navigation/stack";
import { useMutation, useQuery } from "@apollo/client";
import {
    GET_MISSION_VACATION_STATUS,
    GET_PAYROLL_BREAKDOWN,
    GET_PAYROLL_BREAKDOWN_SUMMARY,
    SAVE_PAYROLL_BREAKDOWN,
    SUBMIT_PAYROLL_BREAKDOWN,
} from "../../graphql/timesheet";
import ScreenHeader from "../user/ScreenHeader";
import { useI18n } from "../../hooks/i18n-hooks";
import { usePayrollBreakdownFormatter } from "../../hooks/payroll-beakdown-utils";
import Gap from "../../ui_atoms/Gap";
import Loading from "../../components/Loading";
import {
    BonusInput,
    PayrollBreakdownStatus,
    PayrollSourceType,
    ProfileType,
    Repetition,
    TimeSheetState,
} from "../../__generated__/graphql";
import PayrollBreakdownEditorHourly from "../../components/editors/payroll-breakdown/PayrollBreakdownEditorHourly";
import PayrollBreakdownEditorMonthly from "../../components/editors/payroll-breakdown/PayrollBreakdownEditorMonthly";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { View } from "react-native";
import { SwipeButton } from "../../components/SwipeButton";
import { PayrollBreakdownData } from "../../components/editors/payroll-breakdown/PayrollBreakdownEditor";
import VStack from "../../ui_atoms/VStack";
import { useApolloErrorSnackbar, useSnackbar } from "../../hooks/snackbar";
import { StackActions } from "@react-navigation/native";
import { BodyMediumText } from "../../components/FormattedText";
import ListItem from "../../ui_atoms/ListItem";
import { GET_MISSION } from "../../graphql/core";
import Label from "../../ui_atoms/Label";
import { ProfileSwitcherManagerContext } from "../../contexts/ProfileSwitcherManager";
import MissionInfoItem from "../../components/MissionInfoItem";
import { FULL_DATE_FORMAT, useDateFormatter } from "../../hooks/date";
import PayrollBreakdownSummary from "./PayrollBreakdownSummary";
import { useTracking } from "../../hooks/tracking";
import { buildErrorHeadband } from "../ScreenCardHeadband";
import { GET_ACTIVITY_TODO_FEED, GET_ALL_PROFILES_ACTIVITIES_INFO } from "../../graphql/activity";
import { diff } from "deep-diff";
import { useDebouncedCallback } from "use-debounce";

type PayrollBreakdownEditorScreenProps = StackScreenProps<
    RootStackParamList,
    "PayrollBreakdownEditor"
>;

const EDITORS = {
    hourly: PayrollBreakdownEditorHourly,
    daily: PayrollBreakdownEditorHourly,
    monthly: PayrollBreakdownEditorMonthly,
    package: PayrollBreakdownEditorHourly,
    undefined: null,
};
type PayrollBreakdownDataWithoutAbsences = Omit<
    PayrollBreakdownData,
    "absences" | "bonuses" | "effectiveFees" | "dynamicFees"
>;
const extractDecimalPayrollData = (
    key: keyof PayrollBreakdownDataWithoutAbsences,
    data?: PayrollBreakdownData,
) => {
    const val = data?.[key];
    if (val === undefined || val === null) {
        return undefined;
    }
    return `${val}`;
};

const sanitizeBonuses = <T extends BonusInput>(
    bonuses: Array<T> | undefined,
): Array<BonusInput> | undefined => {
    if (!bonuses) {
        return undefined;
    }
    return bonuses.map((bonus) => {
        return {
            description: bonus.description,
            amount: bonus.amount,
        };
    });
};

const PayrollBreakdownEditorScreen = ({ route, navigation }: PayrollBreakdownEditorScreenProps) => {
    const payrollBreakdownId = route.params.payrollBreakdownId;
    const i18n = useI18n();
    const { trackEvent } = useTracking();
    const screenCardRef = useRef<ScreenCardHandle>(null);
    const { formatMonthYear, formatPayrollDuration } = usePayrollBreakdownFormatter();

    const { data, error, loading } = useQuery(GET_PAYROLL_BREAKDOWN, {
        variables: { payrollBreakdownId },
        skip: !payrollBreakdownId,
        fetchPolicy: "cache-and-network",
    });
    const payrollBreakdown = data?.getPayrollBreakdown;

    const {
        data: vacationStatusData,
        error: vacationStatusError,
        loading: vacationStatusLoading,
    } = useQuery(GET_MISSION_VACATION_STATUS, {
        variables: { missionId: payrollBreakdown?.missionInfo.id || "" },
        skip: !payrollBreakdown,
        fetchPolicy: "cache-and-network",
    });
    const vacationStatus = vacationStatusData?.getMissionVacationStatus;

    const { data: missionData } = useQuery(GET_MISSION, {
        variables: { id: payrollBreakdown?.missionInfo.id || "" },
        skip: !payrollBreakdown?.missionInfo.id,
        fetchPolicy: "cache-and-network",
    });
    const [submitPayrollBreakdown, { loading: processing }] = useMutation(
        SUBMIT_PAYROLL_BREAKDOWN,
        {
            refetchQueries: [GET_ACTIVITY_TODO_FEED, GET_ALL_PROFILES_ACTIVITIES_INFO],
        },
    );
    const [savePayrollBreakdown, { loading: saving, error: savingError }] =
        useMutation(SAVE_PAYROLL_BREAKDOWN);

    useApolloErrorSnackbar(
        savingError,
        undefined,
        i18n.t("screens.payroll_breakdown_editor.error_saving"),
    );

    const onBack = useCallback(() => {
        navigation.goBack();
    }, [navigation]);
    const { profileType } = useContext(ProfileSwitcherManagerContext);
    const formatDate = useDateFormatter();
    const Editor = EDITORS[payrollBreakdown?.missionInfo?.earningType || "undefined"];
    const [validData, setValidData] = React.useState<PayrollBreakdownData | null>(null);
    const [_, setData] = React.useState<PayrollBreakdownData>({});

    const isFromTimesheet = useMemo(() => {
        return payrollBreakdown?.missionInfo.payrollSource === PayrollSourceType.Lift;
    }, [payrollBreakdown]);

    const _buildData = useCallback(
        (data: PayrollBreakdownData) => {
            return !isFromTimesheet
                ? {
                      deltaWork: data.deltaWork,
                      usedVacationAmount: data.usedVacationAmount,
                      usedVacationMinutes: data.usedVacationMinutes,
                      totalWorkedDays: extractDecimalPayrollData("totalWorkedDays", data),
                      totalWork100: data.totalWork100,
                      totalWork125: data.totalWork125,
                      totalWork150: data.totalWork150,
                      absences: data.absences,
                      bonuses: sanitizeBonuses(data.bonuses),
                      effectiveFees: data.effectiveFees,
                      dynamicFees: data.dynamicFees,
                      overtimePaid100: data.overtimePaid100,
                      overtimePaid125: data.overtimePaid125,
                  }
                : {
                      bonuses: sanitizeBonuses(data.bonuses),
                      effectiveFees: data.effectiveFees,
                      dynamicFees: data.dynamicFees,
                      overtimePaid100: data.overtimePaid100,
                      overtimePaid125: data.overtimePaid125,
                  };
        },
        [isFromTimesheet],
    );

    const {
        data: payrollBreakdownData,
        error: payrollBreakdownError,
        loading: payrollBreakdownLoading,
    } = useQuery(GET_PAYROLL_BREAKDOWN_SUMMARY, {
        variables: {
            payrollBreakdownId,
            payrollBreakdownInput: validData ? _buildData(validData) : {},
        },
        skip: !validData,
        fetchPolicy: "cache-and-network",
    });

    const currentContract = missionData?.getMission?.currentContract;

    // mission contains fees of deductions?
    const needWorkDays =
        (currentContract?.fees?.length || 0) > 0 || (currentContract?.deductions?.length || 0) > 0;
    const { showSnackbar } = useSnackbar();

    const handleSave = useCallback(
        async (data: PayrollBreakdownData) => {
            await savePayrollBreakdown({
                variables: {
                    payrollBreakdownId: payrollBreakdownId,
                    data: _buildData(data),
                },
            });
        },
        [_buildData, payrollBreakdownId, savePayrollBreakdown],
    );

    // autosave when user stop changing data for 3 seconds
    const handleSaveDebounced = useDebouncedCallback(handleSave, 3000, {});

    const handleSubmit = useCallback(async () => {
        if (!validData) {
            return;
        }
        void trackEvent("click_submit_payroll_breakdown");
        await submitPayrollBreakdown({
            variables: {
                payrollBreakdownId: payrollBreakdownId,
                data: _buildData(validData),
            },
        });
        showSnackbar({
            text: i18n.t("screens.payroll_breakdown_editor.snackbar_submitted"),
            mode: "success",
        });
        navigation.dispatch(
            StackActions.replace("PayrollBreakdownDetails", {
                payrollBreakdownId: payrollBreakdownId,
            }),
        );
    }, [
        validData,
        trackEvent,
        submitPayrollBreakdown,
        payrollBreakdownId,
        _buildData,
        showSnackbar,
        i18n,
        navigation,
    ]);

    const onChangeData = useCallback(
        (data: PayrollBreakdownData) => {
            setData((old) => {
                if (diff(old, data)) {
                    if (data) {
                        void handleSaveDebounced(data);
                    }
                    return data;
                }
                return old;
            });
        },
        [handleSaveDebounced],
    );

    //no timesheets are considered as completed as for monthly mission we can have the first month without any timesheet.
    const allCompleted =
        payrollBreakdown?.timesheets.length === 0 ||
        payrollBreakdown?.timesheets?.every(
            (ts) => ts.status === TimeSheetState.Submitted || ts.status === TimeSheetState.Accepted,
        );
    const hasOpenTimesheet = payrollBreakdown?.timesheets?.some(
        (ts) => ts.status === TimeSheetState.Open,
    );
    const lastRejectedTimeSheet = payrollBreakdown?.timesheets?.find(
        (ts) => ts.status === TimeSheetState.Rejected,
    );
    const canSubmit = useMemo(() => {
        return (
            (isFromTimesheet && allCompleted) || // in timesheet we check if they are completed
            (!isFromTimesheet && validData && validData.usedVacationMinutes !== undefined) // in external we check that data are set.
        );
    }, [isFromTimesheet, allCompleted, validData]);

    const showSubmitButton = useMemo(() => {
        return (
            profileType === ProfileType.Client &&
            payrollBreakdown?.status == PayrollBreakdownStatus.Open
        );
    }, [payrollBreakdown?.status, profileType]);

    const isEmptyPeriod = useMemo(() => {
        return !!(
            payrollBreakdown?.startDate &&
            payrollBreakdown?.endDate &&
            payrollBreakdown?.startDate > payrollBreakdown?.endDate
        );
    }, [payrollBreakdown]);

    const descriptionStatus = useMemo(() => {
        if (payrollBreakdown?.status === PayrollBreakdownStatus.Submitted) {
            return "submitted";
        } else {
            if (isEmptyPeriod) {
                return "empty_period";
            }
            if (payrollBreakdown?.missionInfo.payrollSource === PayrollSourceType.ManualClient) {
                return "external_ts";
            }
            if (allCompleted) {
                return "lift_ts_completed";
            }
            if (hasOpenTimesheet || lastRejectedTimeSheet) {
                return "lift_ts_open";
            }
        }
    }, [payrollBreakdown, allCompleted, hasOpenTimesheet, lastRejectedTimeSheet, isEmptyPeriod]);

    const subLabel = useMemo(() => {
        if (!payrollBreakdown) {
            return i18n.t("loading");
        }
        return `${formatMonthYear(payrollBreakdown)} (${formatPayrollDuration(payrollBreakdown)})`;
    }, [payrollBreakdown, formatPayrollDuration, formatMonthYear, i18n]);

    const headerLabel = useMemo(() => {
        const repetition = currentContract?.workingTimeClassicSpec?.repetition;
        switch (repetition) {
            case Repetition.None:
                return i18n.t("payroll_breakdown.nav_title_no_repetition");
            case Repetition.Week:
            case Repetition.Month:
            case Repetition.Day:
                return i18n.t("payroll_breakdown.nav_title");
            default:
                return " "; // loading...
        }
    }, [i18n, currentContract?.workingTimeClassicSpec?.repetition]);

    return (
        <View style={{ flex: 1 }}>
            <ScreenHeader
                label={headerLabel}
                subLabel={subLabel}
                onLeftAction={onBack}
                leftActionDisabled={loading || vacationStatusLoading}
                hideProfile
                useSmallLabel
            />
            <ScreenCard
                ref={screenCardRef}
                bottomComponent={
                    showSubmitButton ? (
                        <SwipeButton
                            testID="button-submit-payroll-breakdown"
                            disabled={!canSubmit}
                            processing={processing || loading || vacationStatusLoading || saving}
                            title={i18n.t("payroll_breakdown.button_submit")}
                            onSwipeSuccess={handleSubmit}
                            thumbIconName="swipe-right"
                            mode="success"
                        />
                    ) : null
                }
                headband={
                    error || vacationStatusError
                        ? buildErrorHeadband(i18n.t("screens.payroll_breakdown_editor.cannot_load"))
                        : undefined
                }
            >
                <Gap gap={1} />
                {!payrollBreakdown && (loading || vacationStatusLoading) && <Loading />}
                {payrollBreakdown && vacationStatus && Editor && (
                    <VStack>
                        <VStack h="2/3">
                            <BodyMediumText>
                                {i18n.t(
                                    `screens.payroll_breakdown.info_text.${profileType}.${descriptionStatus}`,
                                    {
                                        workerName: payrollBreakdown.missionInfo?.workerInfo?.name,
                                        clientName:
                                            payrollBreakdown.missionInfo?.clientInfo?.profileName,
                                        companyName: payrollBreakdown.missionInfo?.clientInfo?.name,
                                        date: payrollBreakdown.submittedAt
                                            ? formatDate(
                                                  new Date(payrollBreakdown.submittedAt),
                                                  FULL_DATE_FORMAT,
                                              )
                                            : "",
                                    },
                                )}
                            </BodyMediumText>
                        </VStack>
                        <Gap gap={1} />
                        <MissionInfoItem missionInfo={payrollBreakdown.missionInfo} />
                        {isFromTimesheet && (
                            <ListItem
                                icon="timesheet"
                                description={i18n.t(
                                    "screens.payroll_breakdown.show_timesheets_action",
                                )}
                                badge={
                                    !allCompleted ? (
                                        <Label
                                            mode="warning"
                                            text={i18n.t(
                                                "screens.payroll_breakdown_editor.incomplete_timesheets",
                                            )}
                                        />
                                    ) : undefined
                                }
                                onPress={() => {
                                    navigation.navigate("PayrollBreakdownTimeSheetScreen", {
                                        payrollBreakdownId,
                                    });
                                }}
                            />
                        )}
                        <Gap gap={1} />
                        <Editor
                            payrollBreakdown={payrollBreakdown}
                            mission={missionData?.getMission || undefined}
                            vacationStatus={vacationStatus}
                            profileType={ProfileType.Client}
                            navigation={navigation}
                            onValidData={setValidData}
                            onChange={onChangeData}
                            needWorkDays={needWorkDays}
                            isFromTimesheet={isFromTimesheet}
                            timeSheetsNeedCompletion={!allCompleted}
                            isEmptyPeriod={isEmptyPeriod}
                        />
                        {allCompleted && validData && (
                            <PayrollBreakdownSummary
                                rows={payrollBreakdownData?.getPayrollBreakdownSummary.rows}
                                needCompute={
                                    payrollBreakdownData?.getPayrollBreakdownSummary.needProcessing
                                }
                                loading={payrollBreakdownLoading}
                                error={payrollBreakdownError}
                                contractEarningType={currentContract?.earningType}
                            />
                        )}
                    </VStack>
                )}
            </ScreenCard>
        </View>
    );
};

export default PayrollBreakdownEditorScreen;
